<template>
  <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
    <!-- Business identity -->
    <div class="mb-5">
      <div class="flex items-center gap-3 mb-3">
        <img
          v-if="businessLogoUrl"
          :src="businessLogoUrl"
          :alt="businessName"
          class="w-10 h-10 rounded-lg object-cover"
        />
        <div
          v-else
          class="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center text-white font-semibold text-sm"
        >
          {{ businessInitial }}
        </div>
        <h3 class="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-tight truncate">
          {{ businessName }}
        </h3>
      </div>
    </div>

    <!-- Completion bar -->
    <div class="mb-5">
      <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1.5">
        <span>Progress</span>
        <span>{{ completionPercentage }}%</span>
      </div>
      <div class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          class="h-full bg-primary-500 rounded-full transition-all duration-300"
          :style="{ width: completionPercentage + '%' }"
        />
      </div>
    </div>

    <!-- Step list -->
    <nav>
      <ol class="space-y-1">
        <li
          v-for="(step, index) in steps"
          :key="step.key"
          class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors"
          :class="stepClasses(index)"
          @click="$emit('navigate', index)"
        >
          <!-- Step indicator icon -->
          <span class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium"
                :class="indicatorClasses(index)">
            <svg v-if="isCompleted(step.key)" xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <template v-else>{{ index + 1 }}</template>
          </span>

          <!-- Step label -->
          <span class="truncate">{{ step.label }}</span>
        </li>
      </ol>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  steps: { type: Array, required: true },
  progress: { type: Object, default: () => ({}) },
  currentStepIndex: { type: Number, required: true },
  completionPercentage: { type: Number, default: 0 },
  businessName: { type: String, default: '' },
  businessLogoUrl: { type: String, default: null },
  mode: { type: String, default: 'internal' }
})

defineEmits(['navigate'])

const businessInitial = computed(() => {
  return (props.businessName || '?').charAt(0).toUpperCase()
})

function isCompleted(key) {
  return props.progress[key] === 'completed'
}

function stepClasses(index) {
  if (index === props.currentStepIndex) {
    return 'bg-primary-500/10 text-primary-500 font-medium'
  }
  if (isCompleted(props.steps[index]?.key)) {
    return 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
  }
  return 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
}

function indicatorClasses(index) {
  if (index === props.currentStepIndex) {
    return 'bg-primary-500 text-white'
  }
  if (isCompleted(props.steps[index]?.key)) {
    return 'bg-green-500 text-white'
  }
  return 'border-2 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
}
</script>
